import { motion, useScroll, useTransform, useInView} from "framer-motion";
import { useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCheckCircle, FaRocket, FaThLarge, FaGlobe, FaLink,
  FaShoppingCart, FaUsers, FaFileAlt, FaMobileAlt, FaChartLine,
  FaBrain, FaArrowRight, FaCoins, FaWallet, FaNetworkWired,
  FaLock, FaChartBar, FaCode, FaBoxOpen, FaDatabase, FaCogs,
  FaCloud, FaMicrochip, FaPalette, FaSearch, FaShieldAlt, 
  FaLayerGroup, FaExpand, FaExchangeAlt, FaVoteYea, FaGamepad,
  FaUsersCog, FaFileContract, FaMoneyBillWave, FaTachometerAlt,
} from "react-icons/fa";

/* ─── SERVICE DATA ─────────────────────────────────────────────── */
const serviceDetailsData = {
  "custom-software-development": {
    id: "custom-software-development",
    icon: FaThLarge,
    title: "Custom Software Development",
    shortTitle: "Custom Software",
    tagline: "Tailored enterprise solutions built from scratch",
    description: "We architect and build powerful, scalable software systems exactly aligned with your business logic — no compromises, no generic templates.",
    fullDescription: `Off-the-shelf software forces you to adapt your business to its limitations. We do the opposite. Our custom software development service designs and engineers solutions that fit your exact workflows, user base, and growth trajectory.

From greenfield enterprise platforms to mission-critical business tools, we work through every layer of the stack — from database architecture to pixel-perfect UIs. Our engineering culture prioritizes security, performance, and long-term maintainability over quick fixes.`,
    subServices: [
      { icon: FaCogs, title: "ERP & CRM Systems", desc: "End-to-end enterprise resource planning and customer relationship management platforms built around your operations." },
      { icon: FaDatabase, title: "Database Architecture", desc: "Optimized relational and NoSQL data models for high-volume, mission-critical workloads." },
      { icon: FaCloud, title: "Cloud-Native Apps", desc: "Microservices, serverless functions, and containerized deployments on AWS, GCP, or Azure." },
      { icon: FaShieldAlt, title: "Security Engineering", desc: "Role-based access, end-to-end encryption, compliance frameworks (GDPR, HIPAA, SOC2)." },
      { icon: FaExchangeAlt, title: "API & Integration Layer", desc: "RESTful, GraphQL, and WebSocket APIs integrating your software into any third-party ecosystem." },
      { icon: FaCode, title: "Legacy Modernization", desc: "Refactor and re-architect aging codebases into modern, maintainable systems without downtime." },
    ],
    keyFeatures: [
      "End-to-end software development lifecycle",
      "Scalable and modular architecture",
      "Enterprise-grade security implementation",
      "Integration with existing systems",
      "Cloud-native development",
      "API-first approach",
      "Comprehensive testing and QA",
      "CI/CD pipeline automation",
      "Performance optimization",
      "Long-term maintenance and support"
    ],

    process: [
      { step: "01", title: "Requirements Analysis", desc: "Deep dive into your business needs, user requirements, and technical constraints to define clear scope." },
      { step: "02", title: "Architecture Design", desc: "Create scalable system architecture with technology stack selection and infrastructure planning." },
      { step: "03", title: "Agile Development", desc: "Iterative sprints with demos, feedback loops, and visible progress every two weeks." },
      { step: "04", title: "Quality Assurance", desc: "Automated unit, integration, and E2E tests — plus manual QA for edge cases." },
      { step: "05", title: "Deployment", desc: "Zero-downtime releases with CI/CD pipelines, rollback strategies, and monitoring." },
      { step: "06", title: "Maintenance", desc: "Ongoing support, security patches, performance monitoring, and feature evolution." }
    ],
    benefits: [
      "Perfect Fit for Your Business",
      "Scalable for Future Growth",
      "Competitive Advantage",
      "Reduced Operational Costs",
      "Complete Ownership & Control",
      "Enhanced Security & Compliance"
    ],
    useCases: [
      "Enterprise Resource Planning (ERP)",
      "Customer Relationship Management (CRM)",
      "Supply Chain Management Systems",
      "Healthcare Management Systems",
      "Financial Trading Platforms",
      "Educational Management Systems"
    ],

  },

  "web-development": {
    id: "web-development",
    icon: FaGlobe,
    title: "Web Development",
    shortTitle: "Web Development",
    tagline: "Modern, fast, and conversion-driven web experiences",
    description: "We build digital experiences that look stunning, load instantly, and turn visitors into customers — from landing pages to full-scale SaaS platforms.",
    fullDescription: `Your website is your most powerful sales asset — or your biggest liability. We build modern, responsive web solutions using the latest frameworks that combine beautiful design with technical excellence.

Whether you need a high-converting marketing site, a complex web application, or a real-time platform, our full-stack engineers deliver solutions that perform flawlessly across every device and browser. Speed, SEO, and accessibility are not afterthoughts — they are built in from day one.`,
    subServices: [
      { icon: FaGlobe, title: "Corporate & Landing Pages", desc: "High-conversion websites with compelling design, fast load speeds, and SEO-optimized architecture." },
      { icon: FaTachometerAlt, title: "SaaS Web Applications", desc: "Feature-rich, multi-tenant web apps with authentication, dashboards, billing, and real-time updates." },
      { icon: FaSearch, title: "SEO & Performance", desc: "Core Web Vitals optimization, structured data, technical SEO audits, and page speed tuning." },
      { icon: FaLayerGroup, title: "Progressive Web Apps", desc: "Offline-capable, installable PWAs that bridge the gap between web and native app experiences." },
      { icon: FaDatabase, title: "Headless CMS Integration", desc: "Content-managed sites using Sanity, Contentful, or Strapi with blazing-fast frontends." },
      { icon: FaCode, title: "API & Backend Development", desc: "RESTful and GraphQL APIs, serverless functions, and robust backend logic powering your frontend." },
    ],
    keyFeatures: [
      "Responsive and mobile-first design",
      "Progressive Web Apps (PWA)",
      "Server-side rendering (SSR)",
      "Static site generation (SSG)",
      "RESTful & GraphQL APIs",
      "Real-time features with WebSockets",
      "SEO optimization",
      "Core Web Vitals optimization",
      "Cross-browser compatibility",
      "Headless CMS integration"
    ],

    process: [
      { step: "01", title: "Discovery", desc: "Understand business goals, target audience, conversion objectives, and technical requirements." },
      { step: "02", title: "UI/UX Design", desc: "Create wireframes, high-fidelity designs, and interactive prototypes for review." },
      { step: "03", title: "Frontend Development", desc: "Build responsive, animated interfaces with modern frameworks and accessibility standards." },
      { step: "04", title: "Backend Development", desc: "Develop robust APIs, database schemas, and server-side logic." },
      { step: "05", title: "Testing & QA", desc: "Cross-browser, cross-device, and performance testing before every release." },
      { step: "06", title: "Launch & Optimize", desc: "Deploy with CDN, monitor Core Web Vitals, and continuously optimize for conversions." }
    ],
    benefits: [
      "24/7 Global Accessibility",
      "Improved Customer Engagement",
      "Higher Conversion Rates",
      "SEO-Friendly Structure",
      "Fast Load Times",
      "Scalable Infrastructure"
    ],
    useCases: [
      "Corporate Websites", "E-commerce Platforms",
      "SaaS Applications", "Portfolio Websites",
      "Blog & Content Platforms", "Social Networks"
    ],

  },

  "blockchain-smart-contracts": {
    id: "blockchain-smart-contracts",
    icon: FaLink,
    title: "Blockchain & Smart Contracts",
    shortTitle: "Blockchain Dev",
    tagline: "Decentralized systems, tokens, DeFi & Web3 — fully built",
    description: "From token launches and MLM smart contracts to DeFi protocols and NFT platforms — we engineer the full blockchain stack for your Web3 vision.",
    fullDescription: `Blockchain is not just about cryptocurrency — it's a new paradigm for trust, ownership, and financial infrastructure. At Chain Magician Lab, we engineer complete blockchain ecosystems: from smart contract logic and tokenomics to full DApp frontends and multi-level referral systems.

We build the complex stuff — presale platforms with vesting schedules, MLM or referral reward contracts, investment pool systems, DAO governance, and cross-chain bridges. Our audited, gas-optimized contracts run on Ethereum, BSC, Polygon, Solana, and more.`,
    subServices: [
      { icon: FaCoins, title: "Token Development (ERC-20/BEP-20/SPL)", desc: "Custom fungible tokens with deflationary mechanics, taxes, max wallet limits, anti-bot protection, and full smart contract audit." },
      { icon: FaMoneyBillWave, title: "Presale & ICO Platforms", desc: "Tiered presale contracts with whitelist phases, soft/hard caps, vesting schedules, referral bonuses, and auto-listing on DEXes." },
      { icon: FaNetworkWired, title: "MLM & Referral Systems", desc: "Multi-level marketing smart contracts with configurable depth levels, commission splits, downline tracking, and automated payouts on-chain." },
      { icon: FaChartBar, title: "Investment Plans & Staking", desc: "Fixed/flexible staking pools, compound interest contracts, ROI investment plans, locked liquidity vaults, and yield farming systems." },
      { icon: FaWallet, title: "Wallet & Swap Integration", desc: "MetaMask, WalletConnect, Trust Wallet integration with custom DEX aggregators, token swap interfaces, and portfolio dashboards." },
      { icon: FaLock, title: "DeFi Protocols", desc: "Lending/borrowing platforms, liquidity pools, AMM contracts, flash loan systems, and governance token distribution." },
      { icon: FaVoteYea, title: "DAO & Governance", desc: "On-chain voting contracts, proposal systems, treasury management, and timelock-controlled upgrades." },
      { icon: FaGamepad, title: "NFT & GameFi", desc: "ERC-721/ERC-1155 NFT contracts, marketplace smart contracts, play-to-earn reward systems, and NFT staking mechanisms." },
    ],
    keyFeatures: [
      "Smart contract development (Solidity/Rust)",
      "Presale & ICO platforms with vesting",
      "MLM multi-level referral contracts",
      "Staking & investment pool contracts",
      "Token creation with custom tokenomics",
      "Wallet & WalletConnect integration",
      "DeFi protocol implementation",
      "Cross-chain bridge development",
      "Smart contract security audits",
      "Layer 2 & gas optimization"
    ],

    process: [
      { step: "01", title: "Discovery", desc: "Map your blockchain vision — tokenomics, contract logic, referral tiers, and user flows." },
      { step: "02", title: "Architecture", desc: "Design contract inheritance structure, proxy patterns, and chain selection." },
      { step: "03", title: "Smart Contract Dev", desc: "Write secure, gas-optimized contracts with comprehensive unit and integration tests." },
      { step: "04", title: "Frontend Integration", desc: "Build wallet-connected DApp interfaces with real-time contract event listeners." },
      { step: "05", title: "Security Audit", desc: "Internal + third-party audits, reentrancy checks, and fuzz testing before mainnet." },
      { step: "06", title: "Deployment & Launch", desc: "Mainnet deployment, contract verification, monitoring dashboards, and post-launch support." }
    ],
    benefits: [
      "Fully Decentralized & Trustless",
      "On-Chain Transparency",
      "Automated MLM Payouts",
      "Immutable Investment Records",
      "Global 24/7 Accessibility",
      "Zero-Intermediary Transactions"
    ],
    useCases: [
      "Token Launch & Presale", "MLM / Referral Platforms",
      "Staking & Yield Farming", "NFT Marketplace",
      "DeFi Lending Protocols", "DAO Governance Systems"
    ],

  },

  "ecommerce-solutions": {
    id: "ecommerce-solutions",
    icon: FaShoppingCart,
    title: "E-Commerce Solutions",
    shortTitle: "E-Commerce",
    tagline: "Powerful online stores built to scale and convert",
    description: "Custom e-commerce platforms with advanced inventory, multi-vendor support, payment gateways, and conversion-optimized checkout flows.",
    fullDescription: `A great e-commerce platform is an engine for revenue — not just a product catalogue. We build custom online stores that convert browsers into buyers through optimized UX, lightning-fast load times, and seamless checkout experiences.

From single-brand stores to complex multi-vendor marketplaces, we integrate payment gateways, shipping APIs, inventory systems, and loyalty programs into cohesive, scalable platforms. Every element is designed to maximize average order value and repeat purchase rate.`,
    subServices: [
      { icon: FaShoppingCart, title: "Custom Storefront Development", desc: "Bespoke e-commerce platforms built from scratch with your exact product catalog, branding, and UX requirements." },
      { icon: FaMoneyBillWave, title: "Payment Gateway Integration", desc: "Stripe, PayPal, Razorpay, crypto payments — multi-currency checkout with fraud detection and PCI compliance." },
      { icon: FaBoxOpen, title: "Inventory & Order Management", desc: "Real-time inventory tracking, automated restocking alerts, multi-warehouse support, and order fulfillment workflows." },
      { icon: FaUsers, title: "Multi-Vendor Marketplace", desc: "Seller onboarding, commission management, vendor dashboards, and dispute resolution systems." },
      { icon: FaChartBar, title: "Analytics & Personalization", desc: "Sales dashboards, funnel analytics, abandoned cart recovery, and AI-powered product recommendations." },
      { icon: FaMobileAlt, title: "Mobile Commerce", desc: "Native iOS/Android shopping apps or PWAs with push notifications, wishlists, and one-tap checkout." },
    ],
    keyFeatures: [
      "Custom storefront development",
      "Multi-gateway payment processing",
      "Inventory & order management",
      "Shopping cart optimization",
      "Multi-vendor marketplace",
      "Discount, coupon & loyalty systems",
      "Abandoned cart recovery",
      "Product recommendation engine",
      "Mobile-responsive & PWA",
      "SEO & analytics integration"
    ],

    process: [
      { step: "01", title: "Product Discovery", desc: "Map your catalog, customer journey, payment flows, and business model." },
      { step: "02", title: "Conversion-First Design", desc: "Design product pages, checkout funnels, and mobile UX optimized for high AOV." },
      { step: "03", title: "Platform Development", desc: "Build storefront, admin panel, inventory system, and vendor portal." },
      { step: "04", title: "Integrations", desc: "Connect payment gateways, shipping providers, ERP, and analytics tools." },
      { step: "05", title: "Performance Testing", desc: "Load testing, checkout flow QA, and cross-device compatibility checks." },
      { step: "06", title: "Launch & Scale", desc: "Deploy, monitor conversion metrics, and optimize continuously." }
    ],
    benefits: [
      "Increased Sales & Revenue",
      "Seamless Checkout Experience",
      "Mobile Shopping Optimization",
      "Scalable for Business Growth",
      "Data-Driven Insights",
      "Enhanced Customer Loyalty"
    ],
    useCases: [
      "B2C Online Stores", "B2B E-commerce Portals",
      "Multi-vendor Marketplaces", "Subscription Stores",
      "Digital Product Stores", "Dropshipping Platforms"
    ],

  },

  "ux-ui-design": {
    id: "ux-ui-design",
    icon: FaUsers,
    title: "UX/UI Design",
    shortTitle: "UX/UI Design",
    tagline: "Interfaces that users love and businesses rely on",
    description: "User-centered design that solves real problems — from wireframes to pixel-perfect design systems, built for beauty and measurable business impact.",
    fullDescription: `Great design reduces friction, builds trust, and drives action. We design interfaces that users find intuitive from the first interaction — combining deep user research with visual craftsmanship to produce experiences that convert.

Our UX/UI practice covers the full design lifecycle: discovery and research, information architecture, wireframing, high-fidelity design, interactive prototyping, usability testing, and design system creation. Every design decision is traceable back to user needs and business outcomes.`,
    subServices: [
      { icon: FaSearch, title: "UX Research & Audits", desc: "User interviews, heatmap analysis, usability testing, and competitor benchmarking to identify friction and opportunity." },
      { icon: FaLayerGroup, title: "Wireframing & Prototyping", desc: "Low and high-fidelity wireframes with clickable Figma prototypes for stakeholder alignment and user testing." },
      { icon: FaPalette, title: "Visual Design & Branding", desc: "Brand identity systems, color theory application, typography hierarchies, and illustration systems." },
      { icon: FaCogs, title: "Design Systems", desc: "Scalable component libraries in Figma with Storybook documentation for consistent, maintainable UI across products." },
      { icon: FaMobileAlt, title: "Mobile App Design", desc: "iOS and Android UI following platform guidelines while maintaining brand identity and conversion-focused flows." },
      { icon: FaExpand, title: "Dashboard & Data Viz Design", desc: "Complex admin panels, analytics dashboards, and data visualization designed for clarity and decision speed." },
    ],
    keyFeatures: [
      "User research and persona creation",
      "Wireframing and prototyping",
      "User flow and journey mapping",
      "Information architecture",
      "Visual design and brand systems",
      "Responsive & adaptive design",
      "Usability testing & iteration",
      "Design system creation",
      "Accessibility (WCAG 2.1 AA)",
      "Developer handoff documentation"
    ],

    process: [
      { step: "01", title: "Research", desc: "Understand users, competitors, and business goals through interviews and analysis." },
      { step: "02", title: "IA & User Flows", desc: "Structure content, define navigation, and map user journeys end-to-end." },
      { step: "03", title: "Wireframing", desc: "Low-fidelity layouts validated with stakeholders before any visual polish." },
      { step: "04", title: "Visual Design", desc: "Apply brand systems, color, typography, and motion to create stunning visuals." },
      { step: "05", title: "Prototype & Test", desc: "Clickable prototypes tested with real users for validation and iteration." },
      { step: "06", title: "Handoff & Support", desc: "Developer-ready Figma files with specs, assets, and design system docs." }
    ],
    benefits: [
      "Higher User Satisfaction",
      "Increased Engagement",
      "Reduced Development Costs",
      "Better Conversion Rates",
      "Consistent Brand Experience",
      "Accessibility for All Users"
    ],
    useCases: [
      "Mobile App Design", "Web Application Design",
      "SaaS Dashboard Design", "E-commerce UX",
      "Design System Creation", "Brand Identity"
    ],

  },

  "whitepaper-creation": {
    id: "whitepaper-creation",
    icon: FaFileAlt,
    title: "Whitepaper Creation",
    shortTitle: "Whitepaper",
    tagline: "Authoritative documents that raise capital and build credibility",
    description: "Technical and narrative whitepapers for blockchain projects, token launches, and technology ventures — engineered to convince investors and onboard communities.",
    fullDescription: `A whitepaper is your project's first impression with the world's most critical audience: investors, developers, and early adopters. A poorly written whitepaper kills funding rounds. An exceptional one attracts capital, media coverage, and community trust.

We combine technical depth, narrative craft, and professional design to produce whitepapers that explain your vision clearly, validate your tokenomics, articulate your technical architecture, and build unwavering credibility with your target audience.`,
    subServices: [
      { icon: FaFileAlt, title: "Blockchain / Crypto Whitepapers", desc: "Token utility documentation, consensus mechanism explanation, tokenomics tables, roadmap, and team sections aligned with investor expectations." },
      { icon: FaCoins, title: "Tokenomics Design & Documentation", desc: "Supply schedules, vesting timelines, allocation charts, burn mechanics, and economic model validation with detailed narrative." },
      { icon: FaFileContract, title: "Technical Architecture Papers", desc: "Protocol-level documentation, system diagrams, smart contract architecture, and security model explanation for developer audiences." },
      { icon: FaChartBar, title: "Investment Prospectus", desc: "Market analysis, competitive landscape, revenue model, use of funds breakdown, and regulatory compliance statements." },
      { icon: FaSearch, title: "Research & Industry Reports", desc: "Data-driven, cited industry reports for establishing thought leadership and generating inbound enterprise leads." },
      { icon: FaPalette, title: "Design & Visual Production", desc: "Professional multi-column layouts, infographics, data charts, and publication-ready PDF formatting in your brand." },
    ],
    keyFeatures: [
      "Blockchain & crypto whitepapers",
      "Tokenomics design & documentation",
      "Technical architecture documentation",
      "Investment prospectus writing",
      "Data visualization & infographics",
      "Market research & competitor analysis",
      "SEO-optimized online versions",
      "Multi-format publishing (PDF, web)",
      "Professional editing & proofreading",
      "Legal disclaimer sections"
    ],

    process: [
      { step: "01", title: "Discovery", desc: "Deep-dive into your project, technology, target audience, and funding goals." },
      { step: "02", title: "Research", desc: "Market analysis, competitor review, and technical feasibility documentation." },
      { step: "03", title: "Outline & Structure", desc: "Logical document architecture with section hierarchy and key arguments mapped." },
      { step: "04", title: "Content Writing", desc: "Technical and narrative content written with precise, investor-grade language." },
      { step: "05", title: "Design & Visuals", desc: "Charts, diagrams, infographics, and professional multi-column layout design." },
      { step: "06", title: "Review & Publish", desc: "Multiple revision rounds, final edit, and multi-channel publishing support." }
    ],
    benefits: [
      "Establish Thought Leadership",
      "Build Credibility & Trust",
      "Educate Investors & Community",
      "Generate Quality Leads",
      "Support Fundraising Rounds",
      "Differentiate from Competitors"
    ],
    useCases: [
      "Blockchain Project Launch", "Token Sale Documentation",
      "Technology Solution Papers", "Investment Prospectus",
      "Industry Research Reports", "Protocol Specification Docs"
    ],

  },

  "application-development": {
    id: "application-development",
    icon: FaMobileAlt,
    title: "Application Development",
    shortTitle: "App Development",
    tagline: "Native & cross-platform apps that dominate app stores",
    description: "iOS, Android, and cross-platform mobile apps — built for performance, beautiful UX, and scalable backends that grow with your user base.",
    fullDescription: `Your app competes in a marketplace with millions of alternatives. Mediocre is invisible. We build mobile and desktop applications that offer genuine native performance, polished UX, and features that keep users coming back.

Whether you need a consumer lifestyle app, a B2B productivity tool, a healthcare platform, or a real-time social experience, our team builds applications that respect platform guidelines while delivering distinctly branded, high-performance experiences.`,
    subServices: [
      { icon: FaMobileAlt, title: "iOS App Development (Swift)", desc: "Native Swift applications with SwiftUI, Core Data, HealthKit, ARKit, and full App Store optimization and submission support." },
      { icon: FaMobileAlt, title: "Android App Development (Kotlin)", desc: "Native Kotlin apps following Material Design, leveraging Jetpack Compose, Room, and Google Play optimization." },
      { icon: FaLayerGroup, title: "Cross-Platform (React Native)", desc: "Single codebase for iOS and Android with near-native performance, live updates via Expo/EAS, and shared backend integration." },
      { icon: FaCode, title: "Flutter Development", desc: "Beautiful, expressive UI with Flutter's widget system — pixel-perfect across iOS, Android, web, and desktop." },
      { icon: FaCloud, title: "Backend & API for Apps", desc: "Firebase, Supabase, or custom Node.js/Django backends with push notifications, real-time sync, and auth systems." },
      { icon: FaChartBar, title: "App Analytics & ASO", desc: "In-app analytics setup, crash reporting, App Store optimization, and A/B testing for ratings and installs." },
    ],
    keyFeatures: [
      "Native iOS development (Swift/SwiftUI)",
      "Native Android (Kotlin/Jetpack Compose)",
      "Cross-platform (React Native / Flutter)",
      "Offline-first architecture",
      "Push notification systems",
      "Biometric & social authentication",
      "Real-time sync & WebSockets",
      "App Store & Play Store submission",
      "App Store Optimization (ASO)",
      "In-app purchases & subscriptions"
    ],

    process: [
      { step: "01", title: "Strategy & Scope", desc: "Define platform, feature set, monetization model, and target audience metrics." },
      { step: "02", title: "UI/UX Design", desc: "Platform-specific design with brand consistency and conversion-focused user flows." },
      { step: "03", title: "Development", desc: "Agile builds with weekly releases to TestFlight/Firebase Test Lab for continuous feedback." },
      { step: "04", title: "QA & Device Testing", desc: "Real-device testing matrix across 20+ device/OS combinations." },
      { step: "05", title: "Store Submission", desc: "App Store and Play Store submission with optimized listings, screenshots, and metadata." },
      { step: "06", title: "Maintenance", desc: "OS update compatibility, crash monitoring, and feature roadmap execution." }
    ],
    benefits: [
      "Native-Level Performance",
      "Platform-Optimized UX",
      "App Store Visibility",
      "Offline Capability",
      "Push Notification Engagement",
      "Device Hardware Integration"
    ],
    useCases: [
      "Consumer Lifestyle Apps", "B2B Productivity Tools",
      "Healthcare & Fitness Apps", "Fintech & Crypto Apps",
      "Social Networking Apps", "On-Demand Service Apps"
    ],

  },

  "consulting-strategy": {
    id: "consulting-strategy",
    icon: FaChartLine,
    title: "Consulting & Strategy",
    shortTitle: "Consulting",
    tagline: "Expert technology guidance that protects investment and accelerates growth",
    description: "Feasibility studies, technology architecture reviews, digital transformation roadmaps, and startup advisory — decisions grounded in experience, not guesswork.",
    fullDescription: `The most expensive technology mistakes are the ones made before a line of code is written. Our consulting practice helps you navigate complex technology decisions with confidence — from choosing the right stack to restructuring legacy systems, from blockchain feasibility to AI adoption strategy.

We bring battle-tested experience across dozens of industries to help you build the right thing, in the right order, at the right cost. Our deliverables are always actionable: clear recommendations, prioritized roadmaps, and risk-aware implementation plans.`,
    subServices: [
      { icon: FaChartLine, title: "Technology Strategy & Roadmapping", desc: "Multi-year technology roadmaps with phased milestones, budget estimates, team requirements, and decision checkpoints." },
      { icon: FaSearch, title: "Feasibility Studies", desc: "Technical feasibility, market validation, cost-benefit analysis, and risk assessment before committing to development." },
      { icon: FaShieldAlt, title: "Architecture Reviews", desc: "Independent audit of existing systems — scalability, security posture, technical debt assessment, and modernization recommendations." },
      { icon: FaCloud, title: "Cloud Migration Strategy", desc: "Cloud-readiness assessment, lift-and-shift vs refactor decision framework, multi-cloud architecture, and cost optimization plans." },
      { icon: FaBrain, title: "AI & Blockchain Adoption", desc: "Identify highest-ROI AI/ML use cases, evaluate blockchain applicability, and build phased adoption roadmaps with measurable KPIs." },
      { icon: FaUsersCog, title: "Team & Process Consulting", desc: "Engineering team structure recommendations, agile process implementation, hiring strategy, and toolchain optimization." },
    ],
    keyFeatures: [
      "Technology strategy development",
      "Feasibility studies & validation",
      "Technology stack assessment",
      "Architecture & security review",
      "Digital transformation planning",
      "Risk assessment & mitigation",
      "Cost-benefit analysis",
      "Vendor selection support",
      "Implementation roadmaps",
      "Team training & mentoring"
    ],

    process: [
      { step: "01", title: "Discovery", desc: "Stakeholder interviews, system inventory, and business goal alignment sessions." },
      { step: "02", title: "Assessment", desc: "Evaluate current systems, team capabilities, processes, and technical debt." },
      { step: "03", title: "Analysis & Options", desc: "Map options with trade-offs, risks, costs, and expected business outcomes." },
      { step: "04", title: "Strategy Development", desc: "Comprehensive written strategy with clear, prioritized recommendations." },
      { step: "05", title: "Roadmap Creation", desc: "Quarter-by-quarter implementation roadmap with milestones and success metrics." },
      { step: "06", title: "Execution Support", desc: "Embedded advisory during implementation with checkpoint reviews." }
    ],
    benefits: [
      "Informed Technology Decisions",
      "Reduced Investment Risk",
      "Optimized Technology Spend",
      "Clear Implementation Path",
      "Business-Aligned Technology",
      "Competitive Positioning"
    ],
    useCases: [
      "Digital Transformation", "Technology Modernization",
      "Blockchain Adoption Planning", "Cloud Migration",
      "AI Implementation Strategy", "Startup Tech Advisory"
    ],

  },

  "ai-machine-learning": {
    id: "ai-machine-learning",
    icon: FaBrain,
    title: "AI & Machine Learning",
    shortTitle: "AI / ML",
    tagline: "Intelligent systems that learn, predict, and automate",
    description: "Custom AI models, NLP pipelines, computer vision systems, and LLM-powered applications built to solve real business problems at production scale.",
    fullDescription: `Artificial Intelligence moves fastest when it solves a specific, well-defined business problem. We don't sell generic AI — we build custom models and intelligent systems engineered around your data, your users, and your measurable outcomes.

From predictive churn models and fraud detection to LLM-powered copilots and real-time recommendation engines, our ML engineers design, train, deploy, and monitor AI systems that deliver consistent, reliable value in production environments.`,
    subServices: [
      { icon: FaBrain, title: "LLM & Generative AI Apps", desc: "Custom ChatGPT-style assistants, document Q&A systems, RAG pipelines, and LLM fine-tuning on your domain data using OpenAI, Claude, or open-source models." },
      { icon: FaChartBar, title: "Predictive Analytics", desc: "Customer churn prediction, demand forecasting, pricing optimization, and financial risk models with explainable outputs." },
      { icon: FaSearch, title: "Natural Language Processing", desc: "Sentiment analysis, entity extraction, text classification, semantic search, and multi-language NLP pipelines." },
      { icon: FaMicrochip, title: "Computer Vision", desc: "Object detection, image classification, OCR, quality inspection automation, and real-time video analysis systems." },
      { icon: FaNetworkWired, title: "Recommendation Systems", desc: "Collaborative filtering, content-based, and hybrid recommendation engines for e-commerce, media, and SaaS platforms." },
      { icon: FaCogs, title: "MLOps & Model Deployment", desc: "Model serving infrastructure, A/B testing frameworks, drift detection, retraining pipelines, and production monitoring dashboards." },
    ],
    keyFeatures: [
      "Custom LLM applications & RAG",
      "Predictive analytics models",
      "Natural language processing",
      "Computer vision systems",
      "Recommendation engines",
      "Anomaly & fraud detection",
      "Time series forecasting",
      "Chatbot & conversational AI",
      "Model optimization & compression",
      "MLOps & production deployment"
    ],

    process: [
      { step: "01", title: "Problem Definition", desc: "Define the exact business problem, success metrics, and data requirements." },
      { step: "02", title: "Data Engineering", desc: "Data collection, cleaning, labeling, and feature engineering pipelines." },
      { step: "03", title: "Model Development", desc: "Experiment with architectures, train models, and compare against baselines." },
      { step: "04", title: "Validation", desc: "Rigorous evaluation against holdout sets, bias testing, and business metric validation." },
      { step: "05", title: "Deployment", desc: "Model serving via REST API or embedded SDK with CI/CD for model updates." },
      { step: "06", title: "Monitoring", desc: "Production monitoring, drift detection alerts, and scheduled retraining pipelines." }
    ],
    benefits: [
      "Data-Driven Decisions",
      "Automated Operations",
      "Personalized Experiences",
      "Predictive Business Intelligence",
      "Significant Cost Reduction",
      "Sustainable Competitive Edge"
    ],
    useCases: [
      "Customer Churn Prediction", "Fraud Detection",
      "Personalized Recommendations", "Document Processing",
      "Sentiment Analysis", "Predictive Maintenance"
    ],

  }
};

/* ─── ANIMATION VARIANTS ─────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};
const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};
const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};
const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } }
};
const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } }
};

/* ─── ANIMATED SECTION WRAPPER ─────────────────────────────────── */
function AnimSection({ children, delay = 0, variant = fadeUp, className = "", style = {} }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      variants={variant}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      transition={{ delay }}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}

/* ─── STYLES ──────────────────────────────────────────────────────── */
const S = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&display=swap');

  :root {
    --g: #12AF09; --gd: #0d8a07; --g-glow: rgba(18,175,9,0.25);
    --ink: #0d0d0b; --dust: #f4f3ef; --paper: #fff; --border: #e0dfd9;
    --muted: #888;
  }

  .sd-root { font-family: 'Sora', sans-serif; background: var(--dust); color: var(--ink); overflow-x: hidden; }
  .sd-wrap { max-width: 1280px; margin: 0 auto; padding: 0 56px; }
  @media(max-width:768px) { .sd-wrap { padding: 0 24px; } }

  /* HERO */
  .sd-hero {
    min-height: 92vh; background: var(--ink); position: relative;
    overflow: hidden; display: flex; flex-direction: column; justify-content: flex-end;
  }
  .sd-hero-noise {
    position: absolute; inset: 0; pointer-events: none;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
    opacity: 0.5;
  }
  .sd-hero-glow {
    position: absolute; width: 700px; height: 700px; border-radius: 50%;
    background: radial-gradient(circle, rgba(18,175,9,0.12) 0%, transparent 70%);
    top: -200px; right: -200px; pointer-events: none;
  }
  .sd-hero-bg-word {
    position: absolute; bottom: -40px; left: -10px;
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(80px, 16vw, 280px); font-weight: 700;
    letter-spacing: -.07em; color: rgba(255,255,255,0.035);
    white-space: nowrap; pointer-events: none; user-select: none; line-height: 1;
  }
  .sd-hero-inner {
    position: relative; z-index: 4;
    padding: 80px 56px 72px;
    display: grid; grid-template-columns: 1fr auto; align-items: end; gap: 60px;
  }
  @media(max-width:900px) { .sd-hero-inner { grid-template-columns: 1fr; padding: 60px 24px 56px; } }

  .sd-eyebrow {
    display: inline-flex; align-items: center; gap: 12px;
    font-size: 11px; font-weight: 700; letter-spacing: .2em;
    text-transform: uppercase; color: var(--g); margin-bottom: 28px;
  }
  .sd-eyebrow-line { width: 40px; height: 1px; background: var(--g); }

  .sd-hero-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(52px, 7.5vw, 110px); font-weight: 700;
    line-height: .9; letter-spacing: -.03em; color: #fff; margin-bottom: 24px;
  }
  .sd-hero-title .stroke { -webkit-text-stroke: 1.5px rgba(255,255,255,.6); color: transparent; }
  .sd-hero-title .accent { color: var(--g); }

  .sd-hero-desc { font-size: 16px; line-height: 1.8; color: rgba(255,255,255,.5); max-width: 520px; margin-bottom: 36px; }

  .sd-hero-cta { display: flex; gap: 12px; flex-wrap: wrap; }

  .sd-hero-icon-box {
    width: 160px; height: 160px; border-radius: 24px;
    border: 1px solid rgba(255,255,255,.08);
    background: rgba(255,255,255,.04);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  @media(max-width:900px) { .sd-hero-icon-box { display: none; } }
  .sd-hero-icon { font-size: 64px; color: var(--g); }

  /* SCROLL INDICATOR */
  .sd-scroll-line {
    position: absolute; bottom: 0; left: 56px;
    width: 1px; height: 80px; background: linear-gradient(to bottom, transparent, var(--g));
  }

  /* SHARED SECTION */
  .sd-section { padding: 96px 0; }
  .sd-section-sm { padding: 72px 0; }
  @media(max-width:768px) { .sd-section { padding: 64px 0; } }

  .sd-label {
    display: inline-flex; align-items: center; gap: 10px;
    font-size: 11px; font-weight: 700; letter-spacing: .18em;
    text-transform: uppercase; color: var(--g); margin-bottom: 16px;
  }
  .sd-label::before { content:''; display:block; width:28px; height:1px; background:var(--g); }

  .sd-dh {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(34px, 4.5vw, 58px); font-weight: 700;
    line-height: 1.05; letter-spacing: -.025em; color: var(--ink);
  }
  .sd-dh em { font-style: italic; color: var(--g); }
  .sd-dh.white { color: #fff; }

  /* FULL DESC */
  .sd-desc-section { background: #fff; }
  .sd-full-desc {
    font-size: 17px; line-height: 1.9; color: #555; max-width: 820px;
    white-space: pre-line;
  }

  /* SUB SERVICES GRID */
  .sd-sub-section { background: var(--ink); }
  .sd-sub-header { margin-bottom: 56px; }
  .sd-sub-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px;
    background: rgba(255,255,255,.06); border-radius: 16px; overflow: hidden;
  }
  @media(max-width:900px) { .sd-sub-grid { grid-template-columns: repeat(2, 1fr); } }
  @media(max-width:540px) { .sd-sub-grid { grid-template-columns: 1fr; } }

  .sd-sub-card {
    background: rgba(255,255,255,.02); padding: 36px 28px;
    border: 1px solid rgba(255,255,255,.05);
    transition: background .25s;
    position: relative; overflow: hidden; cursor: default;
  }
  .sd-sub-card::before {
    content: ''; position: absolute; left: 0; top: 0; width: 3px; height: 0;
    background: var(--g); transition: height .35s ease;
  }
  .sd-sub-card:hover::before { height: 100%; }
  .sd-sub-card:hover { background: rgba(18,175,9,.04); }

  .sd-sub-icon {
    width: 48px; height: 48px; border-radius: 12px;
    background: rgba(18,175,9,.12); display: flex; align-items: center;
    justify-content: center; margin-bottom: 20px;
    font-size: 20px; color: var(--g);
  }
  .sd-sub-title { font-size: 16px; font-weight: 700; color: #fff; margin-bottom: 10px; }
  .sd-sub-desc { font-size: 13px; line-height: 1.75; color: rgba(255,255,255,.42); }

  /* FEATURES + TECH */
  .sd-feat-section { background: var(--dust); }
  .sd-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start; }
  @media(max-width:900px) { .sd-2col { grid-template-columns: 1fr; gap: 48px; } }

  .sd-feat-list { display: flex; flex-direction: column; gap: 10px; margin-top: 28px; }
  .sd-feat-item {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 16px; border-radius: 8px; background: #fff;
    border: 1px solid var(--border); font-size: 14px; font-weight: 500; color: var(--ink);
    transition: border-color .2s, transform .2s;
  }
  .sd-feat-item:hover { border-color: var(--g); transform: translateX(4px); }
  .sd-feat-check { color: var(--g); flex-shrink: 0; font-size: 14px; }

  .sd-tech-list { display: flex; flex-direction: column; gap: 10px; margin-top: 28px; }
  .sd-tech-item {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px; border-radius: 8px; background: #fff;
    border: 1px solid var(--border); transition: border-color .2s;
  }
  .sd-tech-item:hover { border-color: var(--g); }
  .sd-tech-name { font-size: 14px; font-weight: 600; color: var(--ink); }
  .sd-tech-badge {
    font-size: 10px; font-weight: 700; letter-spacing: .1em;
    padding: 4px 10px; border-radius: 20px; text-transform: uppercase;
  }
  .sd-tech-badge.expert { background: rgba(18,175,9,.12); color: var(--g); }
  .sd-tech-badge.advanced { background: rgba(0,0,0,.06); color: #555; }

  /* PROCESS */
  .sd-process-section { background: #fff; }
  .sd-process-grid {
    display: grid; grid-template-columns: repeat(3,1fr); gap: 2px;
    margin-top: 56px; background: var(--border); border-radius: 16px; overflow: hidden;
  }
  @media(max-width:900px) { .sd-process-grid { grid-template-columns: repeat(2,1fr); } }
  @media(max-width:540px) { .sd-process-grid { grid-template-columns: 1fr; } }

  .sd-process-card {
    background: #fff; padding: 36px 28px; position: relative; overflow: hidden;
    transition: background .2s;
  }
  .sd-process-card:hover { background: #fafaf8; }
  .sd-process-num {
    font-family: 'Cormorant Garamond', serif;
    font-size: 72px; font-weight: 700; line-height: 1; letter-spacing: -.04em;
    color: rgba(0,0,0,.05); position: absolute; top: 12px; right: 20px;
    pointer-events: none; user-select: none;
  }
  .sd-process-step-label {
    font-size: 10px; font-weight: 700; letter-spacing: .18em;
    text-transform: uppercase; color: var(--g); margin-bottom: 14px;
  }
  .sd-process-title { font-size: 18px; font-weight: 700; color: var(--ink); margin-bottom: 10px; }
  .sd-process-desc { font-size: 13px; line-height: 1.75; color: #666; }

  /* BENEFITS + USECASES */
  .sd-bu-section { background: var(--dust); }
  .sd-benefit-list { display: flex; flex-direction: column; gap: 12px; margin-top: 28px; }
  .sd-benefit-item {
    display: flex; align-items: center; gap: 14px;
    font-size: 15px; font-weight: 600; color: var(--ink);
    padding: 14px 18px; border-radius: 8px; background: #fff;
    border: 1px solid var(--border);
  }
  .sd-benefit-icon { color: var(--g); font-size: 14px; flex-shrink: 0; }

  .sd-usecase-grid { display: flex; flex-direction: column; gap: 10px; margin-top: 28px; }
  .sd-usecase-item {
    display: flex; align-items: center; gap: 12px;
    padding: 13px 18px; border-radius: 8px; background: #fff;
    border: 1px solid var(--border); font-size: 14px; color: #444; font-weight: 500;
    transition: border-color .2s;
  }
  .sd-usecase-item:hover { border-color: var(--g); }
  .sd-usecase-check { color: var(--g); font-size: 12px; flex-shrink: 0; }

  /* CASE STUDY */
  .sd-case-section { background: var(--ink); }
  .sd-case-card {
    background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.08);
    border-radius: 20px; padding: 56px; position: relative; overflow: hidden;
  }
  .sd-case-card::before {
    content: '"'; position: absolute; top: -20px; right: 40px;
    font-family: 'Cormorant Garamond', serif; font-size: 240px; font-weight: 700;
    color: rgba(18,175,9,.06); line-height: 1; pointer-events: none;
  }
  @media(max-width:768px) { .sd-case-card { padding: 36px 28px; } }
  .sd-case-header { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; }
  .sd-case-trophy { color: var(--g); font-size: 28px; }
  .sd-case-title { font-size: 24px; font-weight: 700; color: #fff; margin-bottom: 12px; }
  .sd-case-desc { font-size: 15px; line-height: 1.75; color: rgba(255,255,255,.5); margin-bottom: 40px; max-width: 640px; }
  .sd-case-metrics { display: grid; grid-template-columns: repeat(4,1fr); gap: 24px; }
  @media(max-width:768px) { .sd-case-metrics { grid-template-columns: repeat(2,1fr); } }
  .sd-metric {
    padding: 24px 20px; border: 1px solid rgba(255,255,255,.07);
    border-radius: 12px; text-align: center; background: rgba(255,255,255,.02);
  }
  .sd-metric-val {
    font-family: 'Cormorant Garamond', serif;
    font-size: 22px; font-weight: 700; color: var(--g); line-height: 1.2;
  }

  /* CTA */
  .sd-cta { background: var(--ink); position: relative; overflow: hidden; }
  .sd-cta::before {
    content: 'BUILD'; position: absolute; bottom: -80px; right: -40px;
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(100px, 22vw, 340px); font-weight: 700; letter-spacing: -.04em;
    color: rgba(255,255,255,.02); pointer-events: none; user-select: none;
  }
  .sd-cta-inner {
    padding: 100px 56px; display: flex; align-items: flex-end;
    justify-content: space-between; gap: 40px; flex-wrap: wrap; position: relative; z-index: 2;
  }
  @media(max-width:768px) { .sd-cta-inner { padding: 64px 24px; } }
  .sd-cta-h {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(44px, 7vw, 88px); font-weight: 700;
    line-height: .95; letter-spacing: -.03em; color: #fff;
  }
  .sd-cta-h em { color: var(--g); font-style: italic; }
  .sd-cta-btns { display: flex; gap: 12px; flex-wrap: wrap; flex-shrink: 0; }

  /* BUTTONS */
  .btn-g {
    display: inline-flex; align-items: center; gap: 9px;
    background: var(--g); color: #fff;
    padding: 14px 28px; border-radius: 6px;
    font-weight: 700; font-size: 14px; letter-spacing: .01em;
    border: none; cursor: pointer; text-decoration: none;
    transition: transform .2s, box-shadow .2s;
    font-family: 'Sora', sans-serif;
  }
  .btn-g:hover { transform: translateY(-2px); box-shadow: 0 12px 36px rgba(18,175,9,.32); }
  .btn-ghost {
    display: inline-flex; align-items: center; gap: 9px;
    background: transparent; color: rgba(255,255,255,.6);
    padding: 14px 28px; border-radius: 6px;
    font-weight: 600; font-size: 14px;
    border: 1px solid rgba(255,255,255,.14); cursor: pointer; text-decoration: none;
    transition: border-color .2s, color .2s; font-family: 'Sora', sans-serif;
  }
  .btn-ghost:hover { border-color: var(--g); color: var(--g); }
  .btn-outline {
    display: inline-flex; align-items: center; gap: 9px;
    background: transparent; color: var(--ink);
    padding: 14px 28px; border-radius: 6px;
    font-weight: 600; font-size: 14px;
    border: 1px solid var(--border); cursor: pointer; text-decoration: none;
    transition: border-color .2s, color .2s; font-family: 'Sora', sans-serif;
  }
  .btn-outline:hover { border-color: var(--g); color: var(--g); }

  /* DIVIDER */
  .sd-divider { width: 100%; height: 1px; background: var(--border); }

  /* SECTION HEADER */
  .sd-sec-head { margin-bottom: 52px; }
  .sd-sec-sub { font-size: 15px; color: #777; line-height: 1.7; margin-top: 12px; max-width: 520px; }
`;

/* ─── MAIN COMPONENT ──────────────────────────────────────────── */
export default function ServiceDetails() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgWordY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const glowScale = useTransform(scrollYProgress, [0, 1], [1, 1.4]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const service = serviceDetailsData[serviceId];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!service) navigate("/");
  }, [service, navigate]);

  if (!service) return null;

  const Icon = service.icon;

  return (
    <div className="sd-root">
      <style>{S}</style>

      {/* ═══ HERO ════════════════════════════════════════════ */}
      <div className="sd-hero" ref={heroRef}>
        <div className="sd-hero-noise" />
        <motion.div className="sd-hero-glow" style={{ scale: glowScale }} />
        <motion.div className="sd-hero-bg-word" style={{ y: bgWordY }}>
          {service.shortTitle.toUpperCase()}
        </motion.div>
        <div className="sd-scroll-line" />

        <motion.div className="sd-hero-inner" style={{ opacity: heroOpacity }}>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="show"
          >
            <motion.div variants={fadeUp} className="sd-eyebrow">
              <span className="sd-eyebrow-line" />
              Our Service
            </motion.div>

            <motion.h1 variants={fadeUp} className="sd-hero-title">
              <span className="stroke">{service.shortTitle}</span><br />
              <span className="accent">{service.tagline}</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="sd-hero-desc">
              {service.description}
            </motion.p>

            <motion.div variants={fadeUp} className="sd-hero-cta">
              <a href="https://wa.me/916393561379" className="btn-g" target="_blank" rel="noopener noreferrer">
                Start Project <FaArrowRight />
              </a>
              <button className="btn-ghost" onClick={() => document.getElementById('sd-process')?.scrollIntoView({ behavior: 'smooth' })}>
                How It Works
              </button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="sd-hero-icon-box"
          >
            <Icon className="sd-hero-icon" />
          </motion.div>
        </motion.div>
      </div>

      {/* ═══ FULL DESCRIPTION ════════════════════════════════ */}
      <div className="sd-desc-section sd-section">
        <div className="sd-wrap">
          <AnimSection>
            <div className="sd-label">Overview</div>
            <p className="sd-full-desc">{service.fullDescription}</p>
          </AnimSection>
        </div>
      </div>

      {/* ═══ SUB SERVICES ════════════════════════════════════ */}
      {service.subServices && (
        <div className="sd-sub-section sd-section">
          <div className="sd-wrap">
            <AnimSection>
              <div className="sd-sub-header">
                <div className="sd-label">What's Included</div>
                <h2 className="sd-dh white">Everything in <em>{service.shortTitle}</em></h2>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,.38)', marginTop: 12, lineHeight: 1.7, maxWidth: 520 }}>
                  A complete breakdown of every capability and deliverable within this service.
                </p>
              </div>
            </AnimSection>

            <motion.div
              className="sd-sub-grid"
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
            >
              {service.subServices.map((sub, i) => {
                const SubIcon = sub.icon;
                return (
                  <motion.div key={i} variants={fadeUp} className="sd-sub-card">
                    <div className="sd-sub-icon"><SubIcon /></div>
                    <div className="sd-sub-title">{sub.title}</div>
                    <div className="sd-sub-desc">{sub.desc}</div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      )}

      {/* ═══ FEATURES + TECH ═════════════════════════════════ */}
      <div className="sd-bu-section sd-section">
  <div className="sd-wrap">

    <div className="sd-2col sd-2col-img">

      {/* LEFT SIDE */}
      <div className="sd-left-col">

        <AnimSection variant={fadeLeft}>
          <div className="sd-label">Why Choose Us</div>
          <h2 className="sd-dh">Key <em>Benefits</em></h2>

          <motion.div
            className="sd-benefit-list"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {service.benefits.map((b, i) => (
              <motion.div key={i} variants={fadeUp} className="sd-benefit-item">
                <FaRocket className="sd-benefit-icon" />
                {b}
              </motion.div>
            ))}
          </motion.div>
        </AnimSection>



      </div>


      {/* RIGHT IMAGE */}
      <div className="sd-right-img" style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <img
          src="/img/worldmap.png"
          alt="service"
          style={{ display: "block", margin: "0 auto" }}
        />
      </div>

    </div>

  </div>
</div>

      {/* ═══ PROCESS ═════════════════════════════════════════ */}
      <div className="sd-process-section sd-section" id="sd-process">
        <div className="sd-wrap">
          <AnimSection>
            <div className="sd-sec-head">
              <div className="sd-label">Our Process</div>
              <h2 className="sd-dh">How We <em>Work</em></h2>
              <p className="sd-sec-sub">A proven methodology for delivering on time, on budget, and beyond expectations.</p>
            </div>
          </AnimSection>

          <motion.div
            className="sd-process-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
          >
            {service.process.map((step, i) => (
              <motion.div key={i} variants={scaleIn} className="sd-process-card">
                <div className="sd-process-num">{step.step}</div>
                <div className="sd-process-step-label">Step {step.step}</div>
                <div className="sd-process-title">{step.title}</div>
                <div className="sd-process-desc">{step.desc}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ═══ BENEFITS + USE CASES ════════════════════════════ */}
      <div className="sd-bu-section sd-section">
        <div className="sd-wrap">
          <div className="sd-2col">
            <AnimSection variant={fadeLeft}>
              <div className="sd-label">Why Choose Us</div>
              <h2 className="sd-dh">Key <em>Benefits</em></h2>
              <motion.div
                className="sd-benefit-list"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                {service.benefits.map((b, i) => (
                  <motion.div key={i} variants={fadeUp} className="sd-benefit-item">
                    <FaRocket className="sd-benefit-icon" />
                    {b}
                  </motion.div>
                ))}
              </motion.div>
            </AnimSection>

            <AnimSection variant={fadeRight} delay={0.15}>
              <div className="sd-label">Applications</div>
              <h2 className="sd-dh">Use <em>Cases</em></h2>
              <motion.div
                className="sd-usecase-grid"
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
              >
                {service.useCases.map((u, i) => (
                  <motion.div key={i} variants={fadeUp} className="sd-usecase-item">
                    <FaCheckCircle className="sd-usecase-check" />
                    {u}
                  </motion.div>
                ))}
              </motion.div>
            </AnimSection>
          </div>
        </div>
      </div>

 

      {/* ═══ CTA ═════════════════════════════════════════════ */}
      <div className="sd-cta">
        <div className="sd-cta-inner">
          <AnimSection variant={fadeLeft}>
            <motion.h2 className="sd-cta-h">
              Ready to build your<br /><em>{service.shortTitle}</em> project?
            </motion.h2>
          </AnimSection>
          <AnimSection variant={fadeRight} delay={0.15}>
            <div className="sd-cta-btns">
              <a href="https://wa.me/916393561379?text=Hello%20I'm%20interested%20in%20your%20services" className="btn-g" target="_blank" rel="noopener noreferrer">
                Start Your Project <FaArrowRight />
              </a>
              <button className="btn-ghost" onClick={() => navigate('/about')}>
                Back to Home
              </button>
            </div>
          </AnimSection>
        </div>
      </div>
    </div>
  );
}